import { makeCygnusApiCall } from "../../../api/api";
import { GridApiProps } from "../../reusable_components/CygnusGrid/CygnusGrid.utils";

/**
 * Fetches the list of clients awaiting approval by the active user.
 * 
 * Converts offset-based pagination to page number and performs an API call
 * to retrieve matching client records with optional search text.
 * 
 * @param {GridApiProps} params - The pagination and search parameters.
 * @param {number} params.offset - Offset for paginated data (0-based).
 * @param {number} params.limit - Number of records to fetch per page.
 * @param {string} params.searchText - Optional search filter for client name or data.
 * 
 * @returns {Promise<{ records: any[]; totalCount: number }>} 
 * An object containing an array of client records and the total count.
 */
export const fetchApprovalClients = async ({
  offset,
  limit,
  searchText,
  sourceCode,
  filter,
}: GridApiProps): Promise<{ records: any[]; totalCount: number }> => {
  const page = Math.floor(offset / limit) + 1;

  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("page_size", String(limit));
  // core params
  if (searchText != null && searchText !== "") params.set("search_text", String(searchText));
  if (sourceCode) params.set("SourceSystemCode", String(sourceCode));

  // helper to append filter keys (supports array, date, scalar)
  const append = (key: string, val: any) => {
    if (val == null || val === "") return;
    if (Array.isArray(val)) {
      if (val.length === 0) return;
      params.set(key, JSON.stringify(val.map(v => String(v).trim())));
    } else if (val instanceof Date) {
      params.set(key, val.toISOString());
    } else {
      params.set(key, String(val));
    }
  };

  Object.entries(filter ?? {}).forEach(([k, v]) => append(k, v));

  // Always request approval-only records for this endpoint
  params.set("SendForApproval", "Y");

  const url = `/form/approval/?${params.toString()}`;

  const response = await makeCygnusApiCall({
    type: "GET",
    url,
    show_message: true,
    customized_success_message: "Fetched approval client data successfully.",
    customized_failure_message: "Unable to fetch approval client data, please try again.",
    customized_error_message: "Error occurred while fetching approval client data. Please contact support.",
    forMDM: true,
  });

  if (response.success) {
    const records: any[] = response.data?.records || [];
    const totalRecords: number = response.data?.total_records || 0;
    return { records, totalCount: totalRecords };
  } else {
    return { records: [], totalCount: 0 };
  }
};